// Component for SideBar Uncommissioned
import React from 'react';

import { Button, Grid, MenuItem, Paper, TextField, Typography, Tooltip } from '@mui/material';
import { theme } from '../../theme/theme';
import { useDispatch } from "react-redux";

import { usePostMetadataQuery, usePostArchiveQuery, usePostBoxSwapQuery, usePostCancelCmdQuery } from "./apiCloudSystem";
import { uncommissionedSummariesSlice } from "../../features/dashboard/apiDashboard";
import BoxSwapSelectionModal from './BoxSwapSelectionModal';
import moment from 'moment';

// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface SideBarUncommissionedProps {
//   cloudSystemDetails : CloudSystem
//   onNeedsGet : func
// }

const SideBarUncommissioned = (props) => {

  const [customer, setCustomer] = React.useState(props.cloudSystemDetails.customer);
  const [shortName, setShortName] = React.useState(props.cloudSystemDetails.shortName);
  const [expectedAPCount, setExpectedAPCount] = React.useState(props.cloudSystemDetails.expectedAPCount);

  const [cloudSystemForUpdate, setCloudSystemForUpdate] = React.useState(null);
  const [needsPost, setNeedsPost] = React.useState(false);

  const [serialForArchive, setSerialForArchive] = React.useState(null);
  const [needsArchive, setNeedsArchive] = React.useState(false);

  const [boxSwapOpen, setBoxSwapOpen] = React.useState(false);
  const [boxSwapForUpdate, setBoxSwapForUpdate] = React.useState(null);
  const [needsBoxSwap, setNeedsBoxSwap] = React.useState(false);
  const [serialForCancel, setSerialForCancel] = React.useState(null);
  const [needsBoxSwapCancel, setNeedsBoxSwapCancel] = React.useState(false);

  const dispatch = useDispatch();

  const {
    data: didUpdate,
    isError: isUpdateError,
    error: updateError,
  } = usePostMetadataQuery(cloudSystemForUpdate, { refetchOnMountOrArgChange: true, skip: !needsPost });

  const {
    isSuccess: didArchive,
    isError: isArchiveError,
    error: archiveError,
  } = usePostArchiveQuery(serialForArchive, { refetchOnMountOrArgChange: true, skip: !needsArchive });

  const {
    isSuccess: didBoxSwap,
    isError: isBoxSwapError,
    error: boxSwapError,
  } = usePostBoxSwapQuery(boxSwapForUpdate, { refetchOnMountOrArgChange: true, skip: !needsBoxSwap });

  const {
    data: didCancel,
    isError: isCancelError,
    error: cancelError,
  } = usePostCancelCmdQuery(serialForCancel, { refetchOnMountOrArgChange: true, skip: !needsBoxSwapCancel });

  React.useEffect(() => {
    if (isUpdateError) {
      console.error(`Error updating metadata: ${updateError}`);
    }
  }, [isUpdateError, updateError]);

  React.useEffect(() => {
    if (isArchiveError) {
      console.error(`Error archiving cloud system: ${archiveError}`);
    }
  }, [isArchiveError, archiveError]);

  React.useEffect(() => {
    if (props.cloudSystemDetails?.customer !== undefined && props.cloudSystemDetails.customer !== customer) {
      setCustomer(props.cloudSystemDetails.customer)
    }
    if (props.cloudSystemDetails?.shortName !== undefined && props.cloudSystemDetails.shortName !== shortName) {
      setShortName(props.cloudSystemDetails.shortName)
    }
    if (props.cloudSystemDetails?.expectedAPCount !== undefined && props.cloudSystemDetails.expectedAPCount !== expectedAPCount) {
      setExpectedAPCount(props.cloudSystemDetails.expectedAPCount)
    }
  }, [props.cloudSystemDetails.customer, props.cloudSystemDetails.shortName, props.cloudSystemDetails.expectedAPCount]);

  React.useEffect(() => {
    if (didUpdate || didCancel || didBoxSwap) {
      // Trigger GET immediately
      if (props.onNeedsGet) {
        props.onNeedsGet()
      }
    }
  }, [didUpdate, didCancel, didBoxSwap]);

  React.useEffect(() => {
    if (didArchive) {
      // Close the sidebar
      if (props.onClose) {
        props.onNeedsGet(true)
        dispatch(uncommissionedSummariesSlice.actions.decrement());
        props.onClose()
      }
    }
  }, [didArchive])

  const handleApplyChanges = () => {
    var forUpdate = {
      "serialNumber": props.cloudSystemDetails.serialNumber,
      "shortName": shortName !== props.cloudSystemDetails.shortName ? shortName : undefined,
      "customer": customer !== props.cloudSystemDetails.customer ? customer : undefined,
      "expectedAPCount": isHSPrefix ? undefined : (expectedAPCount !== props.cloudSystemDetails.expectedAPCount ? expectedAPCount : undefined),
      "isCommissioned": true,
    };

    setCloudSystemForUpdate(forUpdate);
    setNeedsPost(true);
    dispatch(uncommissionedSummariesSlice.actions.decrement());
  };
  
  const handleArchiveClicked = () => {
    if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
      var forArchive = {
        serialNumber: props.cloudSystemDetails.serialNumber,
        trigger: serialForArchive?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
      };
      setSerialForArchive(forArchive);
      setNeedsArchive(true);
    } else {
      console.error("Error archiving CloudSystem");
    }
  }

  const handleSwapClicked = () => {
    setBoxSwapOpen(true)
  }

  const handleSwapCancelClicked = () => {
    var forCancel = {
      serialNumber: props.cloudSystemDetails.serialNumber,
      commandActionID: boxSwapAction.commandActionID,
      trigger: serialForCancel?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
    };
    setSerialForCancel(forCancel)
    setNeedsBoxSwapCancel(true)
  }

  const handleSwapClosed = () => {
    setBoxSwapOpen(false)
  }

  const handleSwapSaved = (selection) => {
    // selection.targetSerial: serial number of device to swap with
    // selection.archiveTarget: boolean for whether we should archive the target after swapping

    console.debug(`Initiating box swap with selection`, selection)
    setBoxSwapForUpdate({
      ...selection,
      serialNumber: props.cloudSystemDetails.serialNumber,
    })
    setNeedsBoxSwap(true)

    setBoxSwapOpen(false)
  }

  const handleCustomerChanged = (event) => {
    setCustomer(event.target.value);
  };

  const handleShortNameChanged = (event) => {
    setShortName(event.target.value);
  };

  const handleExpectedAPCountChanged = (event) => {
    setExpectedAPCount(event.target.value);
  };

  // Copy some actions UI elements from SideBarActions
  const convertTimestamp = (datetime) => {
    if (datetime) {
      return moment(datetime).format("MM/DD/YY hh:mm:ss a")
    } else {
      return "-- --"
    }
  }

  const renderStatusFromState = (state) => {
    switch (state) {
      case "Unsent":
      case "WaitingForAck":
        return "Command Queued";
      case "Acknowledged":
        return "Command Accepted";
      case "Finished":
        return "Command Completed";
      case "Failed":
        return "Command Rejected";
      case "Cancelled":
        return "Command Cancelled";
      default:
        return "-- --"
    }
  }

  const getContainerParamsFromState = (state) => {
    var params = {
      "border": `1px solid ${theme.palette.tertiary.main}`,
      "backgroundColor": theme.palette.sidebarContent.main,
    };

    switch (state) {
      case "Unsent":
      case "WaitingForAck":
      case "Acknowledged":
        break;
      case "Finished":
        params.border = `1px solid ${theme.palette.success.main}`;
        params.backgroundColor = theme.palette.success.withOpacity;
        break;
      case "Failed":
        params.border = `1px solid ${theme.palette.error.main}`;
        params.backgroundColor = theme.palette.error.withOpacity;
        break;
      case "Cancelled":
        params.border = `1px solid ${theme.palette.error.main}`;
        params.backgroundColor = theme.palette.error.withOpacity;
        break;
      default:
        // Default to Unsent state
        break;
    }

    return params;
  }

  // Generate details view row from single [label, value] pair
  const createCommandDetailsRow = (key, value, index) => {
    return (
      <Grid key={index} container item direction={'row'}>
        <Grid item>
          <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
            {key}
          </Typography>
        </Grid>
        <Grid sx={{ marginLeft: 'auto' }}>
          <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
            {/* Index 0 means status row */}
            {index === 0 ? renderStatusFromState(value) : value ?? "-- --"}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  // Generate a uniform details view from [label, value] array 
  const createCommandDetailsContainer = (keyValuePairs, actionStatus) => {
    // Apply styling to this container based on command state
    var styling = getContainerParamsFromState(actionStatus);
    return (
      <Grid container item sx={{ border: styling.border, backgroundColor: styling.backgroundColor, borderRadius: '4px', p: 2, my: 2 }}>
        {keyValuePairs.map((keyValuePair, index) => {
          return createCommandDetailsRow(keyValuePair[0], keyValuePair[1], index);
        })}
      </Grid>
    );
  }

  const isHSPrefix = props.cloudSystemDetails?.serialNumber?.startsWith("HS-") ?? false;
  const isBoxSwapEnabledVersion = props.cloudSystemDetails?.activeStatus?.softwareVersion > "2.1-000.0"
  const boxSwapAction = props.cloudSystemDetails?.actions?.find((a) => a.method === 'box-swap')

  const createBoxSwapSection = () => {
    var buttonParams = {
      "text": "Swap",
      "disabled": false,
      "backgroundColor": theme.palette.tertiary.main,
      "action": handleSwapClicked,
    };

    if (boxSwapAction) {
      switch (boxSwapAction.status) {
        case "Unsent":
        case "WaitingForAck":
          buttonParams.text = "Cancel";
          buttonParams.disabled = false;
          buttonParams.backgroundColor = theme.palette.sidebarContent.cancel;
          buttonParams.action = handleSwapCancelClicked
          break;
        case "Acknowledged":
          buttonParams.text = "Cancel";
          buttonParams.disabled = true;
          buttonParams.backgroundColor = theme.palette.sidebarContent.disabled;
          buttonParams.action = handleSwapCancelClicked
          break;
        case "Finished":
        case "Failed":
        case "Cancelled":
        default:
          buttonParams.text = "Swap";
          buttonParams.disabled = false;
          buttonParams.backgroundColor = theme.palette.tertiary.main;
          break;
      }
    }

    return (
    <Grid container direction="row" alignItems={'center'} sx={{ marginTop: 2, paddingTop: 2, borderTop: '1px solid #28343E' }}>
      <Grid item>
        <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>
        Swap Device
        </Typography>
      </Grid>
      <Grid item sx={{ marginLeft: "auto" }}>
        {isBoxSwapEnabledVersion &&
        <Button onClick={buttonParams.action} sx={{
          color: theme.palette.tertiary.contrastText,
          backgroundColor: buttonParams.backgroundColor,
          width: '8em',
        }}>
        {buttonParams.text}
        </Button>
        }
        {!isBoxSwapEnabledVersion &&
        <Tooltip title="Cloudport must be updated to enable box swap">
          <span>
          <Button disabled title="Cloudport must be updated to enable box swap" sx={{
              color: theme.palette.tertiary.contrastText,
              backgroundColor: theme.palette.tertiary.main,
              width: '8em',
          }}>
            Swap
          </Button>
          </span>
        </Tooltip>
        }
        <BoxSwapSelectionModal
          open={boxSwapOpen}
          onClose={handleSwapClosed}
          onSave={handleSwapSaved}
          serialNumber={props.cloudSystemDetails?.serialNumber}
          lastStatusUpdate={props.cloudSystemDetails?.lastStatusUpdate}/>
      </Grid>
      {boxSwapAction && createCommandDetailsContainer([['Status', boxSwapAction?.status], ['Timestamp', convertTimestamp(boxSwapAction?.updatedAt)]], boxSwapAction?.status)}
    </Grid>)
  }

  return (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.primary.main
        }}
    >
        <Grid container sx={{ marginTop: 2 }}>
          <Grid container direction="row" alignItems={'center'}>
            <Grid item>
              <Typography sx={{ color: theme.palette.primary.contrastText }}>
                  Customer
              </Typography>
            </Grid>
            <Grid item sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <TextField 
                onChange={handleCustomerChanged}
                value={customer}
                fullWidth
                sx={{ 
                  "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                  input: { 
                    color: theme.palette.primary.contrastText 
                  },
                  textAlignLast: 'end'
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.primary.contrastText }}>
                  Short Name
              </Typography>
            </Grid>
            <Grid item sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <TextField 
                onChange={handleShortNameChanged}
                value={shortName}
                fullWidth
                sx={{ 
                  "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                  input: { 
                    color: theme.palette.primary.contrastText, 
                  }, 
                  textAlignLast: 'end'
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.primary.contrastText }}>
                  Expected AP Count
              </Typography>
            </Grid>
            <Grid item sx={{ marginLeft: 'auto' }}>
              {isHSPrefix && (
                <Typography sx={{ color: theme.palette.primary.contrastText, marginLeft: 'auto', my: 1 }}>
                    {expectedAPCount ?? 0}
                </Typography>
              )}
              {!isHSPrefix && (
                /* Input  */
                <TextField 
                  select
                  onChange={handleExpectedAPCountChanged}
                  value={expectedAPCount ?? 0}
                  fullWidth
                  sx={{ 
                    "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                    "& .MuiOutlinedInput-input":{color: 'white'},
                    "& .MuiSelect-icon":{color: 'white'},
                    textAlignLast: 'end'
                  }}
                >
                  {[0, 1, 2, 3, 4].map((op) => { 
                    return (
                      <MenuItem key={op} value={op}>
                        {op}
                      </MenuItem>
                    )
                  })}
                </TextField>
              )}
            </Grid>
          </Grid>
          <Grid container direction="row" alignItems={'center'} sx={{ marginTop: 4, borderTop: '1 solid #707070' }}>
            <Grid item>
              <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>
                Commission Device
              </Typography>
            </Grid>
            <Grid item sx={{ marginLeft: "auto" }}>
              <Button onClick={handleApplyChanges} sx={{
                  color: theme.palette.tertiary.contrastText,
                  backgroundColor: theme.palette.tertiary.main,
                  width: '8em',
              }}>
                Commission
              </Button>
            </Grid>
          </Grid>
          <Grid container direction="row" alignItems={'center'} sx={{ marginTop: 2, paddingTop: 2, borderTop: '1px solid #28343E' }}>
            <Grid item>
              <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>
              Archive Device
              </Typography>
            </Grid>
            <Grid item sx={{ marginLeft: "auto" }}>
              <Button onClick={handleArchiveClicked} sx={{
                  color: theme.palette.tertiary.contrastText,
                  backgroundColor: theme.palette.tertiary.main,
                  width: '8em',
              }}>
                Archive
              </Button>
            </Grid>
          </Grid>
          {createBoxSwapSection()}
        </Grid>
    </Paper>
  );
};

export default SideBarUncommissioned;